// src/Login/Login.js

import React, { useState, useEffect, useContext } from 'react';
import supabase from '../supabaseClient';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { useTheme } from '@mui/material/styles';
import './login.css';
import { useLocation, useNavigate } from 'react-router-dom';
import UserContext from '../contexts/userContext';

function LoginPage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext); // Get user from context

  // Extract 'redirectTo' from query parameters
  const queryParams = new URLSearchParams(location.search);
  const redirectToParam = queryParams.get('redirectTo') || '/';

  // Build the full redirect URL (must be a full URL for Supabase)
  const redirectToFullUrl = `${window.location.origin}${redirectToParam}`;

  console.log('Redirecting to:', redirectToFullUrl);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check if the user is already logged in and redirect if so
  useEffect(() => {
    if (user) {
      // Redirect to the desired page
      navigate(redirectToParam, { replace: true });
    }
  }, [user, navigate, redirectToParam]);

  const isDark = theme.palette.mode === 'dark';
  const accentPrimary = isDark ? '#BB86FC' : '#6366f1';
  const accentSecondary = isDark ? '#03DAC6' : '#8b5cf6';
  const glowColor = isDark
    ? 'rgba(187, 134, 252, 0.15)'
    : 'rgba(99, 102, 241, 0.1)';

  const containerStyles = {
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: '20px',
    width: isMobile ? '95vw' : '420px',
    maxWidth: '95vw',
    padding: isMobile
      ? theme.spacing(3)
      : `${theme.spacing(4)} ${theme.spacing(5)}`,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: isDark
      ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.99) 0%, rgba(248, 250, 252, 0.98) 100%)',
    boxShadow: `0 0 0 1px ${glowColor}, 0 20px 60px -15px rgba(0,0,0,${isDark ? '0.5' : '0.2'})`,
    backdropFilter: 'blur(12px)',
  };

  const authContainerStyles = {
    width: '100%',
  };

  // Map MUI theme variables to Supabase Auth UI theme variables
  const supabaseTheme = {
    colors: {
      brand: accentPrimary,
      brandAccent: accentSecondary,
      brandButtonText: '#ffffff',
      defaultButtonBackground: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      defaultButtonBackgroundHover: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
      defaultButtonBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
      defaultButtonText: theme.palette.text.primary,
      dividerBackground: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      inputBackground: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
      inputBorderHover: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
      inputBorderFocus: accentPrimary,
      inputText: theme.palette.text.primary,
      inputPlaceholder: theme.palette.text.secondary,
      messageText: theme.palette.text.secondary,
      messageTextDanger: theme.palette.error.main,
      anchorTextColor: accentPrimary,
      anchorTextHoverColor: accentSecondary,
    },
    fonts: {
      bodyFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      buttonFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      inputFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      labelFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
    },
    fontSizes: {
      baseBodySize: '0.9rem',
      baseInputSize: '0.9rem',
      baseLabelSize: '0.8rem',
      baseButtonSize: '0.85rem',
    },
    radii: {
      borderRadiusButton: '12px',
      buttonBorderRadius: '12px',
      inputBorderRadius: '12px',
    },
    borders: {
      inputBorderWidth: '1.5px',
      buttonBorderWidth: '1.5px',
    },
    space: {
      spaceSmall: '8px',
      spaceMedium: '12px',
      spaceLarge: '20px',
      labelBottomMargin: '8px',
      anchorBottomMargin: '8px',
      emailInputSpacing: '8px',
      socialAuthSpacing: '12px',
      buttonPadding: '12px 16px',
      inputPadding: '14px 16px',
    },
  };

  return (
    <div style={containerStyles}>
      <div style={authContainerStyles}>
        <Auth
          redirectTo={redirectToFullUrl}
          supabaseClient={supabase}
          appearance={{
            theme: ThemeSupa,
            variables: {
              [theme.palette.mode === 'light' ? 'default' : 'dark']:
                supabaseTheme,
            },
          }}
          theme={theme.palette.mode === 'light' ? 'default' : 'dark'}
          providers={['google', 'github']}
          localization={{
            variables: {
              sign_in: {
                email_label: 'email',
                email_input_placeholder: 'email address',
                password_label: 'password',
                password_input_placeholder: 'password',
                social_provider_text: `{{provider}}`,
                button_label: 'sign in',
              },
              forgotten_password: {
                link_text: 'forgot password',
              },
              sign_up: {
                link_text: 'sign up',
              },
              provider_names: {
                google: 'google',
                github: 'github',
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export default LoginPage;
